import { useLanguage } from "../../../../stores/LanguageStore";
import {
  Anchor,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import BackButton from "../../../../components/View/BackButton";
import { useLeadsContainerStore } from "../../../../stores/LeadsStore";
import { IconUserOff, IconUsersMinus } from "@tabler/icons-react";
import { useProfileStore } from "../../../../stores/UserStore";
import { importUnsub } from "../../../../api/UnsubscribersAPI";
import { deselectAll, parseProspectDatafields } from "../utils";

const LeadsUnsubscriber = () => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const selectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const setSelectedIds = useLeadsContainerStore((s) => s.setSelectedIds);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem
  );
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpened(false);
  };

  const allSelectedIds = useMemo(() => {
    return Object.values(selectedIds).flatMap((x) => x);
  }, [selectedIds]);

  const handleFinish = useCallback(() => {
    notifications.show({
      message: lang.prm.unsub.saved,
      color: "tertiary.8",
    });
    handleClose();
    setSelectedIds({});

    let newContainers = [...containers];
    // Delete individual IDS
    let selectedIdsColumns = Object.keys(selectedIds);
    selectedIdsColumns.forEach((c) => {
      let i = newContainers.findIndex((x) => x.id.toString() === c.toString());

      if (i > -1) newContainers[i].loading = true;
    });

    deselectAll()
    return setContainers(newContainers);
  }, [containers, selectedIds, setContainers, setSelectedIds, lang]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);
    async function main(current) {
      let unsubData = [];

      let parsedCurrent = parseProspectDatafields(current);

      let url_linkedin = parsedCurrent.properties.find(
        (x) => x.identifier_placeholder === "%linkedin_url%"
      )?.value;

      let email = parsedCurrent.properties.find(
        (x) => x.identifier_placeholder === "%email%"
      )?.value;

      let phone = parsedCurrent.properties.find(
        (x) => x.identifier_placeholder === "%phone%"
      )?.value;

      if (data?.domain === "on") {
        let domain = email.match(/@([^>]+)/)[1];
        unsubData = [
          {
            value: `@${domain}`,
          },
        ];
      }

      if (data?.contact === "on") {
        if (url_linkedin) {
          unsubData.push({
            value: url_linkedin,
          });
        }
        if (email) {
          unsubData.push({
            value: email,
          });
        }
        if (phone) {
          unsubData.push({
            value: phone,
          });
        }

        if (!url_linkedin && !phone && !email) {
          return notifications.show({
            message: lang.prm.unsub.no_data,
            color: "red",
          });
        }
      }

      return importUnsub(
        data?.organization ? parseInt(data?.organization) : null,
        unsubData
      ).then((res) => {
        updateContainerItem(
          current?.custom_status || current?.status,
          current.id,
          {
            unsubscribed: true,
          }
        );
      });
    }

    let requests = [];
    setLoading(true);
    Object.keys(selectedIds).forEach((key) => {
      let ids = selectedIds[key];

      ids.forEach((id) => {
        let item = containers
          .find((x) => x.id === key)
          ?.items.find((x) => x.id === id);

        requests.push(
          new Promise((resolve, reject) => {
            main(item)
              .then((res) => resolve(res))
              .catch((err) => reject(err));
          })
        );
      });
    });

    Promise.allSettled(requests)
      .then((r) => {
        handleFinish();
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {allSelectedIds.length > 0 && (
        <Anchor onClick={() => setOpened(true)} fw={600} c={"red"}>
          <Group gap={8}>
            <IconUsersMinus size={16} />
            {lang.prm.status.contacts.unsubscribe_label}
          </Group>
        </Anchor>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={lang.prm.status.contacts.unsubscribe_text.replace(
          "#",
          allSelectedIds.length
        )}
        centered
      >
        <LoadingOverlay visible={loading} />
        <Stack component={"form"} onSubmit={handleSubmit} gap={"xs"}>
          <Text c={"dimmed"}>{lang.prm.unsub.text}</Text>

          <Stack>
            <Checkbox
              name={"contact"}
              label={lang.prm.unsub.contact_label}
              description={lang.prm.unsub.contact_desc}
              defaultChecked
            />
            <Checkbox
              name={"domain"}
              label={lang.prm.unsub.domain_label}
              description={lang.prm.unsub.domain_desc}
            />
          </Stack>

          <Select
            label={lang.prm.unsub.org}
            placeholder={lang.prm.unsub.org}
            defaultValue={
              profile?.organizations?.length === 1
                ? profile?.organizations?.[0]?.id.toString()
                : ""
            }
            data={profile?.organizations?.map((x) => ({
              label: x.name,
              value: x.id.toString(),
            }))}
            name="organization"
          />

          <Group justify="end" mt={"md"}>
            <BackButton onClick={handleClose} />

            <Button
              leftSection={<IconUserOff size={18} />}
              loading={loading}
              color="red"
              type="submit"
            >
              {lang.prm.unsub.save}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default LeadsUnsubscriber;
